import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal, { ModalHeader, ModalBody, ModalFooter } from 'common/components/modal/Modal';
import { Header, Text } from 'common/components/texts/Texts';
import Button from 'common/components/button/Button';
import Loading from 'common/services/Loading';
import Toast from 'common/services/Toast';
import Logger from 'common/services/Logger';
import { LOGGER_LOG_TYPE } from 'Config';
import BudgetComponentsService, { BudgetComponentCatalogItem } from 'api/budgetComponents/BudgetComponentsService';
import { BudgetLineDto } from 'api/budgets/models/BudgetDto';
import { BudgetLineType } from 'api/budgets/enums/BudgetLineType';
import Utils from 'common/services/Utils';
import { rewriteLineKeyExpressions } from 'store/budget/reducer';
import styles from './ImportComponentModal.module.scss';

interface Props {
    isOpen: boolean;
    onClose: (lines?: BudgetLineDto[], componentId?: string) => void;
}

const ImportComponentModal: React.FC<Props> = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const [components, setComponents] = useState<BudgetComponentCatalogItem[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const loadComponents = async () => {
        try {
            setIsLoading(true);
            const data = await BudgetComponentsService.getCatalog();
            setComponents(data);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t load components', error);
            Toast.error(t('messages.error_load_info'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            setSelectedId(null);
            setSearch('');
            void loadComponents();
        }
    }, [isOpen]);

    const filteredComponents = components.filter(c =>
        c.description.toLowerCase().includes(search.toLowerCase()) ||
        (c.familyName && c.familyName.toLowerCase().includes(search.toLowerCase()))
    );

    const onImport = async () => {
        if (!selectedId) return;

        try {
            Loading.show();

            const component = await BudgetComponentsService.getById(selectedId, true);
            if (!component || !component.lines || component.lines.length === 0) {
                Toast.warning(t('budgets.budget.lines.import_component.empty_component'));
                Loading.hide();
                return;
            }

            // Assign temp keys to lines that don't have one (using id as fallback)
            const sourceLines = component.lines.map(line => ({
                ...line,
                key: line.key || line.id || Utils.newGuid(),
                parentKey: line.parentKey || null,
            }));

            // Build parent mapping from parentId if parentKey is missing
            for (const line of sourceLines) {
                if (!line.parentKey && line.parentId) {
                    const parent = sourceLines.find(l => l.id === line.parentId);
                    if (parent) {
                        line.parentKey = parent.key;
                    }
                }
            }

            // Remap keys to avoid conflicts with existing budget lines
            const keyMapping = new Map<string, string>();
            sourceLines.forEach(line => {
                keyMapping.set(line.key, Utils.newGuid());
            });

            const clearParameterIds = (params: any[]): any[] => {
                if (!params) return [];
                return params.map((p: any) => ({
                    ...p,
                    id: null,
                    budgetLineId: null,
                    children: clearParameterIds(p.children),
                    elements: p.elements?.map((e: any) => ({
                        ...e,
                        id: null,
                        budgetLineParameterId: null,
                        budgetLineParameterMinMaxId: null,
                        parameters: clearParameterIds(e.parameters),
                    })),
                    minMax: p.minMax ? { ...p.minMax, id: null, budgetLineParameterId: null } : undefined,
                }));
            };

            // Root scaffold sections are synthetic — never import them into the budget.
            // Their direct children become the new roots of the imported set.
            const rootSectionKeys = new Set(
                sourceLines
                    .filter(l => l.lineType === BudgetLineType.SECTION
                        && (!l.parentKey || !sourceLines.find(sl => sl.key === l.parentKey)))
                    .map(l => l.key)
            );

            const sourceLinesToImport = sourceLines.filter(l => !rootSectionKeys.has(l.key));

            // Root lines (after stripping) are those with no parent in the import set
            const rootKeys = new Set(
                sourceLinesToImport
                    .filter(l => !l.parentKey
                        || rootSectionKeys.has(l.parentKey)
                        || !sourceLinesToImport.find(sl => sl.key === l.parentKey))
                    .map(l => l.key)
            );

            const remappedLines: BudgetLineDto[] = sourceLinesToImport.map(line => {
                const effectiveParentKey = line.parentKey && !rootSectionKeys.has(line.parentKey)
                    ? line.parentKey
                    : null;
                return {
                    ...line,
                    id: null,
                    key: keyMapping.get(line.key)!,
                    parentKey: effectiveParentKey ? (keyMapping.get(effectiveParentKey) ?? null) : null,
                    sourceBudgetComponentId: rootKeys.has(line.key) ? selectedId : null,
                    parameters: clearParameterIds(line.parameters),
                    checkedForParameters: true,
                    changedParameters: true,
                    isOpen: true,
                };
            });

            // Rewrite expressions to reference the new line keys instead of the original ones
            const finalLines = remappedLines.map(line => rewriteLineKeyExpressions(line, keyMapping));

            Loading.hide();
            onClose(finalLines, selectedId);
        } catch (error: any) {
            Loading.hide();
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t import component', error);
            Toast.error(t('messages.record_save_error'));
        }
    };

    return (
        <Modal isOpen={isOpen} onRequestClose={() => onClose()} style={{ content: { minWidth: 600, maxHeight: '80vh' } }}>
            <div>
                <ModalHeader>
                    <Header>{t('budgets.budget.lines.import_component.title')}</Header>
                </ModalHeader>
                <ModalBody>
                    <div className={styles.searchContainer}>
                        <input
                            type="text"
                            className={styles.searchInput}
                            placeholder={t('common.search')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className={styles.componentsList}>
                        {isLoading && <Text>{t('common.loading')}...</Text>}
                        {!isLoading && filteredComponents.length === 0 && (
                            <Text>{t('budgets.budget.lines.import_component.no_components')}</Text>
                        )}
                        {filteredComponents.map(c => (
                            <div
                                key={c.id}
                                className={`${styles.componentItem} ${selectedId === c.id ? styles.selected : ''}`}
                                onClick={() => setSelectedId(c.id)}
                            >
                                <div className={styles.componentName}>{c.description}</div>
                                {c.familyName && (
                                    <div className={styles.componentFamily}>
                                        {c.familyName}
                                        {c.subFamilyName && ` > ${c.subFamilyName}`}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </ModalBody>
                <ModalFooter className="text-right">
                    <Button variant='secondary' onClick={() => onClose()} style={{ marginRight: '0.5rem' }}>
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={onImport} disabled={!selectedId}>
                        {t('budgets.budget.lines.import_component.import_button')}
                    </Button>
                </ModalFooter>
            </div>
        </Modal>
    );
};

export default ImportComponentModal;
